/* eslint-disable no-plusplus */
/* eslint-disable prefer-arrow-callback */
/* eslint-disable no-nested-ternary */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable react/require-default-props -- defaults vivem nos parâmetros (default params); o eslint-plugin-react não os rastreia através do forwardRef */
import React, {
   forwardRef,
   useEffect,
   useImperativeHandle,
   useMemo,
   useRef,
   useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { listUserDirectory } from '../../services/users/usersApi';
import { notifyApiError } from '../../utils/feedback';
import { formatUserLabel } from '../../utils/format';
import { OptionButton } from '../../styles/ui/Button';

// ---------------------------------------------------------------------------
// Autocomplete de usuário. Consumidor único hoje: o filtro "Usuário" da
// listagem de movimentos.
//
// Fonte: `GET /users/directory?status=all` (handoff 0110 §3.9, 2026-09-15) —
// rota sem RBAC, criada pelo backend justamente para os seletores de usuário
// fora da administração de IAM. `status=all` de propósito: um movimento
// lançado por quem já saiu da empresa continua existindo, e o filtro precisa
// alcançá-lo — esses vêm rotulados "(inativo)".
//
// **A busca é local.** O diretório não tem `q` nem paginação: vem ordenado por
// nome e inteiro. Então a lista é carregada UMA vez (na primeira digitação que
// passa do `minChars`), fica em cache no componente e o filtro roda em
// memória, sem acento e sem caixa. Trocar isso por busca no servidor exigiria
// param novo no backend — não vale para um conjunto deste tamanho.
//
// Histórico: até 2026-09-14 usava `GET /users/search/name` (morta no Gen3);
// entre 14 e 15/09, a listagem administrativa, que exige `iam:user:read`
// (Master-only) e obrigava a degradar em aviso. O diretório eliminou a
// contenção — qualquer autenticado pesquisa.
// ---------------------------------------------------------------------------

// Rótulo canônico da opção: "id - Nome" (+ "(inativo)" quando for ex-usuário).
// Exportado porque a listagem de movimentos monta o chip do filtro aplicado com
// o mesmo texto — duplicar o template deixaria os dois fora de sincronia.
export function userOptionLabel(user) {
   if (!user) return '';
   return `${user.id} - ${formatUserLabel(user)}`;
}

// Normaliza para comparação: sem acento, sem caixa, sem borda em branco.
function normalizeTerm(value) {
   return String(value ?? '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '');
}

const Wrap = styled.div`
   position: relative;
   width: 100%;
`;

const Input = styled.input``; // usa o input do DS via Field (herda o estilo global)

const Dropdown = styled.div`
   position: absolute;
   top: calc(100% + 6px);
   left: 0;
   right: 0;
   z-index: 50;

   border: 1px solid
      ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.12)'};
   border-radius: 10px;
   background: ${({ theme }) => theme?.colors?.surface ?? '#fff'};
   box-shadow: ${({ theme }) =>
      theme?.shadows?.md ?? '0 10px 30px rgba(0,0,0,0.12)'};
   overflow: hidden;
`;

const Row = OptionButton;

const Hint = styled.div`
   padding: 10px 12px;
   font-size: 12px;
   opacity: 0.75;
`;

const UserNameSearch = forwardRef(function UserNameSearch(
   {
      value = '', // user_id selecionado (string/number)
      // Seta o user_id (só ao selecionar / limpar). Recebe um SEGUNDO
      // argumento opcional com o usuário escolhido (`{id, nome}`) ou `null` na
      // limpeza — quem precisa do rótulo (ex.: chip de filtro aplicado da
      // listagem de movimentos) usa; quem não precisa ignora.
      onChange = () => {},
      placeholder = 'Buscar usuário pelo nome...',
      minChars = 3,
      debounceMs = 250,
      limit = 15,
      disabled = false,
   },
   ref
) {
   const [inputValue, setInputValue] = useState(''); // texto visível
   const [items, setItems] = useState([]);
   const [open, setOpen] = useState(false);
   const [loading, setLoading] = useState(false);
   const [activeIndex, setActiveIndex] = useState(-1);

   const [isLocked, setIsLocked] = useState(false); // trava requery após seleção
   const selectedRef = useRef(false); // marca se foi selecionado um item

   const requestIdRef = useRef(0);
   const inputRef = useRef(null);

   // Cache do diretório: uma requisição por montagem do componente, na primeira
   // digitação que passa do `minChars`. A promessa (não a lista) é guardada
   // para que digitar rápido não dispare duas cargas em paralelo.
   const directoryRef = useRef(null);

   // debounce
   const [rawQuery, setRawQuery] = useState('');
   useEffect(() => {
      const t = setTimeout(() => setRawQuery(inputValue), debounceMs);
      return () => clearTimeout(t);
   }, [inputValue, debounceMs]);

   const canSearch = useMemo(() => {
      const q = String(rawQuery || '').trim();
      return q.length >= minChars;
   }, [rawQuery, minChars]);

   useImperativeHandle(ref, () => ({
      lock: () => setIsLocked(true),
      unlock: () => setIsLocked(false),
      clear: () => {
         selectedRef.current = false;
         setIsLocked(false);
         setInputValue('');
         setItems([]);
         setOpen(false);
         setActiveIndex(-1);
         onChange('', null);
      },
      focus: () => inputRef.current?.focus(),
   }));

   // sincroniza mudanças externas do "value" (user_id selecionado)
   const lastValueRef = useRef(String(value ?? ''));

   useEffect(() => {
      const v = String(value ?? '');
      const prev = lastValueRef.current;

      // evita rodar à toa quando o pai renderiza sem mudar o value
      if (v === prev) return;
      lastValueRef.current = v;

      // Se o pai limpou o value (ex.: reset de filtros)
      if (!v) {
         // só faz o reset visual se estávamos "travados" por seleção
         if (selectedRef.current || isLocked) {
            selectedRef.current = false;
            setIsLocked(false);
            setInputValue('');
            setItems([]);
            setOpen(false);
            setActiveIndex(-1);
            setLoading(false);
         }
         return;
      }

      // Se o pai setou um user_id externamente
      // (não temos o nome aqui, então exibimos o ID e travamos)
      selectedRef.current = true;
      setIsLocked(true);

      // só preenche se estiver vazio (pra não sobrescrever "ID - NOME")
      setInputValue((curr) => (curr || v));
   }, [value, isLocked]);

   // ⚠️ Regra de ouro: só seta user_id quando seleciona
   const handleSelect = (u) => {
      if (!u?.id) return;

      selectedRef.current = true;
      setIsLocked(true);
      setOpen(false);
      setItems([]);
      setActiveIndex(-1);

      // UI: mostra "ID - NOME" (com a marca de inativo, quando for o caso)
      setInputValue(userOptionLabel(u));

      // filtro real: user_id (+ o usuário escolhido, para quem quer o rótulo)
      onChange(String(u.id), u);
   };

   const handleBlur = () => {
      // Se não selecionou ninguém, limpa (como você pediu)
      setTimeout(() => {
         if (!selectedRef.current) {
            setInputValue('');
            onChange('');
         }
         setOpen(false);
      }, 120);
   };

   const handleInputChange = (e) => {
      const next = e.target.value;

      // digitou = não está selecionando mais nada
      selectedRef.current = false;
      setIsLocked(false);
      onChange(''); // garante filtro vazio enquanto não escolher um item

      setInputValue(next);
   };

   useEffect(() => {
      if (isLocked) {
         setLoading(false);
         setOpen(false);
         return;
      }

      if (!canSearch) {
         setItems([]);
         setOpen(false);
         setActiveIndex(-1);
         setLoading(false);
         return;
      }

      const term = normalizeTerm(rawQuery);
      const myId = ++requestIdRef.current;

      setLoading(true);

      (async () => {
         try {
            if (!directoryRef.current) {
               directoryRef.current = listUserDirectory({ status: 'all' });
            }
            const list = await directoryRef.current;

            if (requestIdRef.current !== myId) return;

            // Casa por nome (sem acento/caixa) ou pelo id exato — o operador
            // que já sabe o número digita direto.
            const matches = list
               .filter(
                  (user) =>
                     normalizeTerm(user.nome).includes(term) ||
                     String(user.id) === term
               )
               .slice(0, limit);

            setItems(matches);
            setOpen(true);
            setActiveIndex(matches.length ? 0 : -1);
         } catch (err) {
            if (requestIdRef.current !== myId) return;
            // Falhou a carga: não deixa o cache envenenado com a promessa
            // rejeitada, senão a próxima tecla repetiria o erro sem tentar.
            directoryRef.current = null;
            setItems([]);
            setOpen(true);
            setActiveIndex(-1);
            notifyApiError(err, { fallback: 'Erro ao carregar usuários.' });
         } finally {
            if (requestIdRef.current === myId) setLoading(false);
         }
      })();
   }, [isLocked, canSearch, rawQuery, limit]);

   const onKeyDown = (e) => {
      if (!open) return;

      if (e.key === 'ArrowDown') {
         e.preventDefault();
         setActiveIndex((i) => Math.min(i + 1, items.length - 1));
      } else if (e.key === 'ArrowUp') {
         e.preventDefault();
         setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
         e.preventDefault();
         const u = items[activeIndex];
         if (u) handleSelect(u);
      } else if (e.key === 'Escape') {
         setOpen(false);
      }
   };

   return (
      <Wrap>
         <Input
            ref={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            disabled={disabled}
         />

         {open && !disabled && (
            <Dropdown>
               {loading ? (
                  <Hint>Carregando...</Hint>
               ) : items.length ? (
                  items.map((u, idx) => (
                     <Row
                        key={String(u.id)}
                        type="button"
                        $active={idx === activeIndex}
                        // mousedown evita perder o clique por causa do blur
                        onMouseDown={(ev) => ev.preventDefault()}
                        onClick={() => handleSelect(u)}
                     >
                        {userOptionLabel(u)}
                     </Row>
                  ))
               ) : (
                  <Hint>Sem dados</Hint>
               )}
            </Dropdown>
         )}
      </Wrap>
   );
});

UserNameSearch.propTypes = {
   value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   onChange: PropTypes.func,
   placeholder: PropTypes.string,
   minChars: PropTypes.number,
   debounceMs: PropTypes.number,
   limit: PropTypes.number,
   disabled: PropTypes.bool,
};

export default UserNameSearch;
